import React, { useEffect, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import {
  FaTachometerAlt,
  FaBoxOpen,
  FaShoppingCart,
  FaUsers,
  FaBullhorn,
  FaChartLine,
  FaCogs,
  FaFileImport,
  FaSignOutAlt,
  FaPalette,
  FaTimes,
  FaChevronDown,
} from "react-icons/fa";

import { brandLogo } from "../../assets/img/logo/brandLogos";

const defaultLogo = brandLogo;

// match(path) decides which entry is highlighted for the current page.
const exact = (...paths) => (path) => paths.includes(path);
const prefix = (...starts) => (path) => starts.some((start) => path.startsWith(start));

const MENU = [
  { label: "Dashboard", icon: <FaTachometerAlt />, to: "/admin/dashboard", match: exact("/admin/dashboard") },
  {
    id: "products",
    label: "Products",
    icon: <FaBoxOpen />,
    items: [
      { label: "All products", to: "/admin/products", match: (path) => exact("/admin/products")(path) || prefix("/admin/view-product", "/admin/edit-product")(path) },
      { label: "Add product", to: "/admin/add-product", match: exact("/admin/add-product") },
      { label: "Categories", to: "/admin/categories", match: prefix("/admin/categories", "/admin/view-category", "/admin/edit-category", "/admin/add-category") },
      { label: "Inventory stock", to: "/admin/inventory", match: exact("/admin/inventory") },
    ],
  },
  {
    id: "orders",
    label: "Orders",
    icon: <FaShoppingCart />,
    items: [
      { label: "All orders", to: "/admin/orders", match: prefix("/admin/orders") },
      { label: "Sales", to: "/admin/invoice", match: (path) => prefix("/admin/invoice")(path) || exact("/admin/sales", "/admin/show-sales")(path) },
      { label: "Refunds & returns", to: "/admin/refundView", match: prefix("/admin/refund") },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: <FaUsers />,
    items: [
      { label: "All customers", to: "/admin/customers", match: exact("/admin/customers") },
      { label: "Product reviews", to: "/admin/reviews", match: exact("/admin/reviews") },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: <FaBullhorn />,
    items: [
      { label: "Bundles & offers", to: "/admin/bundles", match: exact("/admin/bundles") },
      { label: "Coupons & promos", to: "/admin/coupons", match: exact("/admin/coupons") },
      { label: "Catalog discounts", to: "/admin/discount", match: exact("/admin/discount") },
    ],
  },
  {
    id: "analytics",
    label: "Analytics & finance",
    icon: <FaChartLine />,
    items: [
      { label: "Sales reports", to: "/admin/reports", match: exact("/admin/reports", "/admin/analytics") },
      { label: "Money", to: "/admin/chart_of_account", match: exact("/admin/chart_of_account", "/admin/ledger") },
    ],
  },
  {
    id: "store",
    label: "Online store",
    icon: <FaPalette />,
    items: [
      { label: "Themes", to: "/admin/themes", match: prefix("/admin/themes", "/admin/theme-editor") },
      { label: "SEO audit", to: "/admin/seo", match: exact("/admin/seo") },
      { label: "Search Console", to: "/admin/seo/search-console", match: exact("/admin/seo/search-console") },
    ],
  },
  {
    id: "import",
    label: "Import",
    icon: <FaFileImport />,
    items: [
      { label: "From Shopify", to: "/admin/shopify-import", match: exact("/admin/shopify-import") },
      { label: "From WooCommerce", to: "/admin/woocommerce-import", match: exact("/admin/woocommerce-import") },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: <FaCogs />,
    items: [
      { label: "Store settings", to: "/admin/settings", match: exact("/admin/settings") },
      { label: "Payments", to: "/admin/payments", match: exact("/admin/payments") },
      { label: "Couriers", to: "/admin/couriers", match: exact("/admin/couriers") },
      { label: "Policies", to: "/admin/policies", match: exact("/admin/policies") },
      { label: "Backup", to: "/admin/backup", match: exact("/admin/backup") },
      // Store owner (Super Admin, role_as = 2) only; the API enforces this too.
      { label: "Team users", to: "/admin/team-users", match: exact("/admin/team-users"), ownerOnly: true },
    ],
  },
];

const groupFor = (path) => MENU.find((entry) => entry.items && entry.items.some((item) => item.match(path)));

export default function Sidebar({ open = false, onClose = () => {} }) {
  const location = useLocation();
  const history = useHistory();
  const currentPath = location.pathname;
  const isOwner = sessionStorage.getItem("role") === "superadmin";

  const handleLogout = (e) => {
    e.preventDefault();
    axios.get("/sanctum/csrf-cookie").then(() => {
      axios.post(`/api/logout`).then((res) => {
        sessionStorage.clear();
        localStorage.removeItem("auth_token");
        swal("Logged Out", "You have been safely signed out.", "success", {
          buttons: false,
          timer: 1200,
        });
        history.replace("/admin/login");
      }).catch(() => {
        sessionStorage.clear();
        localStorage.removeItem("auth_token");
        history.replace("/admin/login");
      });
    });
  };

  // The group holding the current page is always open; others open on click.
  const [openGroups, setOpenGroups] = useState(() => {
    const active = groupFor(location.pathname);
    return active ? { [active.id]: true } : {};
  });
  useEffect(() => {
    const active = groupFor(currentPath);
    if (active) setOpenGroups((current) => (current[active.id] ? current : { ...current, [active.id]: true }));
  }, [currentPath]);
  const toggleGroup = (id) => setOpenGroups((current) => ({ ...current, [id]: !current[id] }));

  return (
    <aside className={`admin-sidebar${open ? " is-open" : ""}`}>
      {/* Brand Header */}
      <div className="admin-sidebar-brand">
        <Link to="/admin/dashboard" className="admin-sidebar-logo-link" onClick={onClose}>
          <span className="admin-sidebar-logo">
            <img src={defaultLogo} alt="GetGrowCart" />
          </span>
        </Link>
        <button type="button" className="admin-sidebar-close" aria-label="Close menu" onClick={onClose}>
          <FaTimes size={14} />
        </button>
      </div>

      {/* Navigation List */}
      <nav className="flex-grow-1 py-2" aria-label="Admin menu">
        {MENU.map((entry) => {
          if (!entry.items) {
            return (
              <Link key={entry.to} to={entry.to} onClick={onClose} className={`admin-nav-item ${entry.match(currentPath) ? "active" : ""}`}>
                <span className="admin-nav-icon">{entry.icon}</span>
                <span>{entry.label}</span>
              </Link>
            );
          }
          const hasActive = entry.items.some((item) => item.match(currentPath));
          const isOpen = !!openGroups[entry.id];
          return (
            <div key={entry.id} className={`admin-nav-group${isOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className={`admin-nav-item admin-nav-parent${hasActive ? " has-active" : ""}`}
                onClick={() => toggleGroup(entry.id)}
                aria-expanded={isOpen}
                aria-controls={`admin-nav-${entry.id}`}
              >
                <span className="admin-nav-icon">{entry.icon}</span>
                <span>{entry.label}</span>
                <FaChevronDown className="admin-nav-chevron" size={10} />
              </button>
              <div id={`admin-nav-${entry.id}`} className="admin-nav-sub" hidden={!isOpen}>
                {entry.items.filter((item) => !item.ownerOnly || isOwner).map((item) => (
                  <Link key={item.to} to={item.to} onClick={onClose} className={`admin-nav-subitem ${item.match(currentPath) ? "active" : ""}`}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Logout Footer */}
      <div className="p-3 border-top" style={{ borderColor: "rgba(255, 255, 255, 0.08)" }}>
        <button
          type="button"
          onClick={handleLogout}
          className="admin-nav-item w-100 border-0 text-danger bg-transparent"
          style={{ margin: 0 }}
        >
          <span className="admin-nav-icon"><FaSignOutAlt /></span>
          <span>Log Out</span>
        </button>
      </div>
    </aside>
  );
}
